import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { NotificationManager, prepareRequest } from '@atlas/core';
import { onWithdrawalPaystackSubmit, PREDEFINED_FORMS } from '@atlas/react-forms';
import { parseWithdrawalQuery } from '@atlas/routing';
import { DefaultPopup, getElementsWithLinksFromText, HeaderTitle, Label } from '@atlas/ui';

import FormCom from 'common/Form/FormCom';
import useTranslate from 'common/I18n/Interpreter';

import { displayWithdrawalSuccessNotification } from './withdrawalUtils';

export const WithdrawalPaystackPopup = ({ data: { paymentMethod, redirectUrl }, id }) => {
  const t = useTranslate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { amount, currency, paymentId } = useMemo(
    () => parseWithdrawalQuery(redirectUrl),
    [redirectUrl],
  );
  const additionalValues = useMemo(
    () => ({
      accountType: 'Nuban',
      amount,
      currency,
      paymentId,
    }),
    [amount, paymentId, currency],
  );

  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);
  const handleSubmit = useCallback(
    async value => {
      if (isSubmitting) return;
      const request = prepareRequest({
        method: 'POST',
        url: 'paystack-adapter/v1/payment:create',
      });

      setIsSubmitting(true);

      try {
        const response = await request(value);
        displayWithdrawalSuccessNotification({ amount, currency, paymentMethod, response });
      } catch (error) {
        NotificationManager.display({
          data: {
            headerTitle: 'withdrawal_by_paystack',
            text: 'withdrawal_paystack_text_error',
            title: 'failed_sad_emoticon',
          },
          type: 'error',
        });
      } finally {
        handleClose();
      }
    },
    [amount, currency, handleClose, isSubmitting, paymentMethod],
  );

  const withdrawalPaystackLinkText = t('withdrawal_paystack_link_text');

  const paystackText = useMemo(
    () =>
      getElementsWithLinksFromText(withdrawalPaystackLinkText).map(({ link, text }) => {
        if (link) {
          const path = link.startsWith('/') ? `${window.location.origin}${link}` : link;
          return (
            <a className="link" href={path} key={`${link}`} rel="noreferrer" target="_blank">
              {text}
            </a>
          );
        }

        return (
          <span className="text" key={`${text}`}>
            {text}
          </span>
        );
      }),
    [],
  );

  return (
    <DefaultPopup
      className="withdrawal-paystack"
      headerTitle="withdrawal_by_paystack"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <HeaderTitle className="center block-big withdrawal-paystack__title">
        <div className="title-text">
          <Label message="payment_details" />
        </div>
        <div className="sub-title-text">
          <Label message="please_fill_in_the_fields_below" />
        </div>
      </HeaderTitle>

      <div className="withdrawal-paystack__content">
        <FormCom
          action={onWithdrawalPaystackSubmit}
          additionalValues={additionalValues}
          buttonText="withdrawal"
          disableButton={isSubmitting}
          form={PREDEFINED_FORMS.withdrawalPaystackForm}
          onSubmit={handleSubmit}
        />
        <span className="withdrawal-paystack__text">
          <Label message="withdrawal_paystack_bank_codes_message" />
          &nbsp;
          {paystackText}.
        </span>
      </div>
    </DefaultPopup>
  );
};

WithdrawalPaystackPopup.propTypes = {
  data: PropTypes.shape({
    paymentMethod: PropTypes.string,
    redirectUrl: PropTypes.string,
  }).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
